define((require) => {
    "use strict";

    const Action                    = require("editor/util/Action");
    const ActionSequence            = require("util/ActionSequence");
    const Promise                   = require("shared/util/Promise");
    const _                         = require("underscore");
    const Preconditions             = require("shared/util/Preconditions");
    const GroupModel                = require("shared/ui/groups/GroupModel");
    const Permissions               = require("shared/security/Permissions");
    const DuplicateExperienceAction = require("editor/ui/actions/DuplicateExperienceAction");
    const EditorEvents              = require("shared/services/EditorEvents");
    const ExperienceModel           = require("shared/ui/experience/ExperienceModel");
    const ExportUtilities           = require("../../../editor/ui/settings/export/ExportUtilities").default;
    const MD5                       = require("md5");

    /**
     * Finds the cloned experience for the passed experience id in the cloned experiences array.
     * The cloned experience will always have the "copyOf" attribute equals to the original experience.
     *
     * @param {String} experienceId the experience Id to search for
     * @param {ExperienceModel[]} clonedExperiences the cloned experiences array
     * @returns {ExperienceModel} the cloned experience which originated from the passed experience id.
     */
    function findClonedExperience(experienceId, clonedExperiences) {
        return _.find(clonedExperiences, (clonedExperience) => {
            return clonedExperience.get("copyOf") === experienceId;
        });
    }

    /**
     * Action for duplicating a <tt>BreakpointGroup</tt> and all it's experiences (assets).
     *
     * @class DuplicateBreakpointGroupAction
     */
    return Action.extend({

        constructor: function(group, options) {
            this.__super();
            Preconditions.instanceOf(group, GroupModel, "A valid group instance must be specified.");

            this._group = group;

            this._options = options || {};
        },

        executable: function() {
            return Permissions.Privileges.CREATE.can();
        },

        _performExecution: function() {
            const groupClone             = this._group.clone({duplicate: true}); // duplicate: true -> clear fields like id and version since about to be persisted!
            const groupEntries           = this._group.entries();
            const experiencesToDuplicate = this._group.getSortedAssets();
            const projectContext         = this._options.projectContext;
            const exportedExperienceGroupId = groupClone.get("exportedFromExperienceGroupId");

            // The ordering of the included experiences must be respected - use the ActionSequence (or SyncManager? ping @gro) to
            // ensure experiences are created in sequence, i.e. createdAt get the proper timestamp...
            const actions = _.map(experiencesToDuplicate, (experienceModel) => {
                return new DuplicateExperienceAction(experienceModel);
            });
            // Pass the actions and execute in sequence...
            const    sequence = new ActionSequence(actions);
            return sequence.execute()
                .then((clonedExperiences) => {
                    if (exportedExperienceGroupId) { // only dangerous if you duplicate an export :)
                        groupClone.set("exportedFromExperienceGroupId", exportedExperienceGroupId);
                        groupClone.set("exportedExperienceGroupHash", MD5((+new Date()).toString(36), exportedExperienceGroupId));
                    }

                    const grouplicateHash = groupClone.get("exportedExperienceGroupHash");
                    const groupClonePostfix = exportedExperienceGroupId ? " (exported)" : ` ${this._options.postfix}`;
                    groupClone.set("name", `${groupClone.get("name")} ${groupClonePostfix}`);

                    // Empty the old entries...
                    groupClone.get("entries").reset();
                    /*
                     * Iterate through the original group entries and replace the old experience reference with the new one (the cloned one).
                     * Then, attach the newly cloned entries (#attach will also save the group)
                     * There are two race conditions kinda hidden in here:
                     *      1. both modelWithReplacements.save() AND groupClone.attach(clonedModelWithReplacements, value);
                     *          cause the experiences to be saved, so you can get a 409 by not being careful about
                     *          having one finish before the other
                     *      2. if you call getImageIdsMap() for the same image at the same time, then its possible
                     *          to get duplication in the dashboard..  the underlying code of getImageIdsMap
                     *          now stops this from being possible however.
                     */
                    const attachPromises = _.map(groupEntries, (entry) => {
                        const experienceId = entry[0].get("id");
                        const value = entry[1];
                        const clonedExperience = findClonedExperience(experienceId, clonedExperiences);
                        const experienceIdOfTheClone = clonedExperience.get("id");
                        let returnValue;
                        if (exportedExperienceGroupId) {
                            // get the real experience JSON, mutate it and save it back
                            returnValue = new ExperienceModel({id: experienceIdOfTheClone})
                                .fetch()
                                .done((model) => {
                                    const experienceModel = new ExperienceModel(model.toJSON());
                                    experienceModel.set("name", model.getDisplayName() + " (exported)");
                                    experienceModel.set("exportedFromExperienceGroupId", exportedExperienceGroupId);
                                    experienceModel.set("exportedFromExperienceId", experienceId);
                                    experienceModel.set("exportedExperienceGroupHash", grouplicateHash);
                                    // this DOES NOT GO INTO THE GROUP, its saving changes to the real experience!!
                                    const imageIds = ExportUtilities.getImageIds(experienceModel);
                                    clonedExperience.set(
                                        "name",
                                        // set on the object that goes back into the group
                                        clonedExperience.attributes.name + " (exported)"
                                    );
                                    if (imageIds !== null && imageIds.length !== null && imageIds.length > 0) {
                                        return ExportUtilities
                                            .getImageIdsMap(imageIds)
                                            .then((imageIdsMap) => {
                                                const modelWithReplacements =
                                                    ExportUtilities.replaceImageIdsGivenMap(experienceModel, imageIdsMap);
                                                return modelWithReplacements
                                                    .save()
                                                    .then(() => {
                                                        return imageIdsMap;
                                                    }); // this save generates our 409 if we dont wait here on the save of the Model
                                            })
                                            .then((imageIdsMap) => {
                                                // the below makes the changes to the group
                                                const clonedModelWithReplacements =
                                                    ExportUtilities.replaceImageIdsGivenMap(clonedExperience, imageIdsMap);
                                                // Prevent Breakpoint Save errors by waiting here, cant await return due to lint rule
                                                return groupClone.attach(clonedModelWithReplacements, value); // attach saves the group
                                            });
                                    } // if there were no images, then we just augment the name, dont call the cms api
                                    return groupClone.attach(clonedExperience, value); // attach saves the group
                                });
                        }  else {
                            returnValue = groupClone.attach(clonedExperience, value);
                        }
                        return returnValue;
                    });

                    return Promise.all(attachPromises)
                        .then(async () => { // async/awayt so we hold here, if not then its possible you get to the
                            // dashboard and the Group is not yet saved fully and then you refresh and you see
                            // the rest of the child experiences, cant await return due to lint rule
                            const clonedExperienceReturn = await clonedExperiences;
                            return clonedExperienceReturn;
                        });
                })
                .then(() => {
                    // Attach the group to the project, if in project context.
                    if (projectContext) {
                        projectContext.attach(groupClone);
                    }

                    const eventProperties = {
                        projectId: projectContext && projectContext.project && projectContext.project.get("id"),
                        groupId: groupClone.get("id"),
                        groupPushedVersion: groupClone.get("pushedVersion"),
                        groupType: groupClone.get("type"),
                        groupVersion: groupClone.get("version"),
                        triggerOrigin: "DuplicateGroupButton"
                    };

                    // Trigger a duplicate group event
                    EditorEvents.trigger(EditorEvents.constants.DUPLICATE_GROUP, eventProperties);

                    // Return the newly created group clone
                    return groupClone;
                });
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/actions/DuplicateGroupAction.js